import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';

interface TaskCardProps {
  task: {
    id: number;
    title: string;
    description: string;
    priority: string;
    project_name?: string;
    assignee_name?: string;
    assignee_avatar?: string;
    due_date?: string;
    estimated_hours?: number;
  };
}

const priorityColors: Record<string, string> = {
  low: 'bg-gray-500/10 text-gray-500',
  normal: 'bg-blue-500/10 text-blue-500',
  high: 'bg-orange-500/10 text-orange-500',
  urgent: 'bg-red-500/10 text-red-500',
};

const TaskCard = ({ task }: TaskCardProps) => {
  const initials = task.assignee_name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '??';

  const isOverdue = task.due_date && new Date(task.due_date) < new Date();

  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer bg-background">
      <CardContent className="p-4 space-y-3">
        {/* Priority Badge */}
        <div className="flex items-start justify-between">
          <Badge className={priorityColors[task.priority] || ''}>
            {task.priority}
          </Badge>
        </div>

        {/* Title */}
        <div>
          <h4 className="font-semibold text-sm line-clamp-2">{task.title}</h4>
          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
            {task.description}
          </p>
        </div>

        {/* Project */}
        {task.project_name && (
          <div className="text-xs text-muted-foreground">
            {task.project_name}
          </div>
        )}

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t">
          {/* Assignee */}
          {task.assignee_name && (
            <Avatar className="h-6 w-6">
              <AvatarImage src={task.assignee_avatar} alt={task.assignee_name} />
              <AvatarFallback className="text-xs">{initials}</AvatarFallback>
            </Avatar>
          )}

          {/* Due Date and Estimated Hours */}
          <div className="flex items-center gap-2">
            {task.due_date && (
              <div className={`flex items-center gap-1 text-xs ${isOverdue ? 'text-red-500' : 'text-muted-foreground'}`}>
                <Calendar className="h-3 w-3" />
                <span>{format(new Date(task.due_date), 'MMM d')}</span>
              </div>
            )}
            {task.estimated_hours && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                <span>{task.estimated_hours}h</span>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskCard;
